import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  getSupportTicketById,
  updateTicketStatus,
  assignUsersToTicket,
  addTicketComment,
  updateSupportTicket,
  deleteSupportTicket,
} from "../../Services/supportTicketsService";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";
import StatusChip from "./StatusChip";
import PriorityChip from "./PriorityChip";
import TicketHistoryTimeline from "./TicketHistoryTimeline";
import { statuses, priority, ticketType } from "../../Utils/Utils";

interface TicketDetailsModalProps {
  open: boolean;
  onClose: () => void;
  ticketId: string | null;
  onSuccess: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const TicketDetailsModal: React.FC<TicketDetailsModalProps> = ({
  open,
  onClose,
  ticketId,
  onSuccess,
}) => {
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [statusComment, setStatusComment] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [comment, setComment] = useState("");
  const [resolution, setResolution] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (open && ticketId) {
      fetchTicket();
      fetchUsers();
    }
  }, [open, ticketId]);


  useEffect(() => {
    if (ticket) {
      setSelectedUsers(ticket.assignedTo?.map((u: any) => u._id) || []);
      setSelectedStatus(ticket.status || "");
      setSelectedPriority(ticket.priority || "");
      setSelectedType(ticket.type || "");
      setResolution(ticket.resolution || "");
    }
  }, [ticket]);

  const fetchTicket = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const response: any = await getSupportTicketById(ticketId);
      if (response?.data) {
        setTicket(response?.data);
      }
    } catch (error) {
      console.error("Error fetching ticket:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response: any = await getData(endpoints.user.main);
      if (response?.data) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleAssignUsers = async () => {
    if (!ticketId) return;
    setSubmitting(true);
    try {
      await assignUsersToTicket(ticketId, selectedUsers);
      await fetchTicket();
      onSuccess();
    } catch (error) {
      console.error("Error assigning users:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!ticketId) return;
    setSubmitting(true);
    try {
      await updateTicketStatus(ticketId, selectedStatus, statusComment);
      setStatusComment("");
      await fetchTicket();
      onSuccess();
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePriority = async () => {
    if (!ticketId) return;
    setSubmitting(true);
    try {
      await updateSupportTicket(ticketId, { priority: selectedPriority });
      await fetchTicket();
      onSuccess();
    } catch (error) {
      console.error("Error updating priority:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateType = async () => {
    if (!ticketId) return;
    setSubmitting(true);
    try {
      await updateSupportTicket(ticketId, { type: selectedType });
      await fetchTicket();
      onSuccess();
    } catch (error) {
      console.error("Error updating type:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!ticketId || !comment.trim()) return;
    setSubmitting(true);
    try {
      await addTicketComment(ticketId, comment);
      setComment("");
      await fetchTicket();
      onSuccess();
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateResolution = async () => {
    if (!ticketId) return;
    setSubmitting(true);
    try {
      await updateSupportTicket(ticketId, { resolution });
      await fetchTicket();
      onSuccess();
    } catch (error) {
      console.error("Error updating resolution:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticketId) return;
    setSubmitting(true);
    try {
      await deleteSupportTicket(ticketId);
      setDeleteDialogOpen(false);
      handleClose();
      onSuccess();
    } catch (error) {
      console.error("Error deleting ticket:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setTabValue(0);
    setComment("");
    setStatusComment("");
    setDeleteDialogOpen(false);
    onClose();
  };

  if (!ticket && loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6">{ticket?.ticketNumber}</Typography>
            <Typography variant="body2" color="textSecondary">
              {ticket?.title}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label={Vocabulary.details} />
            <Tab label={Vocabulary.comments} />
            <Tab label={Vocabulary.ticketHistory} />
          </Tabs>
        </Box>

        <DialogContent>
          {/* Details Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {Vocabulary.ticketDescription}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {ticket?.description}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {Vocabulary.resolution}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    placeholder={Vocabulary.resolution}
                  />
                  <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleUpdateResolution}
                      disabled={submitting}
                      startIcon={<EditIcon />}
                    >
                      {Vocabulary.update}
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {Vocabulary.status}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <StatusChip status={ticket?.status} size="medium" />
                  </Box>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={Vocabulary.updateStatus}
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    sx={{ mb: 1 }}
                  >
                    {statuses.map((status) => (
                      <MenuItem value={status.value}>{status.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    label={Vocabulary.comment}
                    value={statusComment}
                    onChange={(e) => setStatusComment(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={handleUpdateStatus}
                    disabled={submitting || selectedStatus === ticket?.status}
                  >
                    {Vocabulary.updateStatus}
                  </Button>
                </Paper>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {Vocabulary.priority}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <PriorityChip priority={ticket?.priority} size="medium" />
                  </Box>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={Vocabulary.updatePriority}
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    sx={{ mb: 1 }}
                  >
                    {priority
                      .filter((p) => p.value !== "")
                      .map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                  </TextField>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={handleUpdatePriority}
                    disabled={submitting || selectedPriority === ticket?.priority}
                  >
                    {Vocabulary.updatePriority}
                  </Button>
                </Paper>

                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {Vocabulary.type}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip label={Vocabulary[ticket?.type]} />
                  </Box>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={Vocabulary.updateType}
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    sx={{ mb: 1 }}
                  >
                    {ticketType
                      .filter((t) => t.value !== "")
                      .map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                  </TextField>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={handleUpdateType}
                    disabled={submitting || selectedType === ticket?.type}
                  >
                    {Vocabulary.updateType}
                  </Button>
                </Paper>

                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {Vocabulary.assignedTo}
                  </Typography>
                  <Autocomplete
                    multiple
                    options={users}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                    value={users.filter((u) => selectedUsers.includes(u._id))}
                    onChange={(_, newValue) => {
                      setSelectedUsers(newValue.map((v) => v._id));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder={Vocabulary.selectUsers} />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={`${option.firstName} ${option.lastName}`}
                          {...getTagProps({ index })}
                          size="small"
                          key={option._id}
                        />
                      ))
                    }
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={handleAssignUsers}
                    disabled={submitting}
                    startIcon={<PersonAddIcon />}
                  >
                    {Vocabulary.assignUsers}
                  </Button>
                </Paper>

                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {Vocabulary.createdBy}
                  </Typography>
                  <Typography variant="body2">
                    {ticket?.createdBy?.email}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {ticket?.createdAt && new Date(ticket.createdAt).toLocaleString()}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2, bgcolor: "#fff3e0", border: "1px solid #ff9800" }}>
                  <Typography variant="subtitle2" gutterBottom color="error">
                    {Vocabulary.dangerZone}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                    {Vocabulary.deleteTicketWarning}
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => setDeleteDialogOpen(true)}
                    startIcon={<DeleteIcon />}
                  >
                    {Vocabulary.deleteTicket}
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Comments Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={Vocabulary.addComment}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={submitting || !comment.trim()}
                  startIcon={<SendIcon />}
                >
                  {Vocabulary.addComment}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              {ticket?.history
                ?.filter((h: any) => h.action === "COMMENTED")
                .map((h: any, index: number) => (
                  <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="subtitle2">
                        {h.performedBy?.firstName} {h.performedBy?.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(h.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {h.comment}
                    </Typography>
                  </Paper>
                ))}
            </Box>
          </TabPanel>

          {/* History Tab */}
          <TabPanel value={tabValue} index={2}>
            <TicketHistoryTimeline history={ticket?.history || []} />
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{Vocabulary.close}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{Vocabulary.confirmDeleteTicket}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {Vocabulary.deleteTicketConfirmation}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>{Vocabulary.ticketNumber}:</strong> {ticket?.ticketNumber}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>{Vocabulary.ticketTitle}:</strong> {ticket?.title}
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: "#ffebee", borderRadius: 1 }}>
            <Typography variant="caption" color="error">
              {Vocabulary.deleteWarningPermanent}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
            {Vocabulary.cancel}
          </Button>
          <Button
            onClick={handleDeleteTicket}
            color="error"
            variant="contained"
            disabled={submitting}
            startIcon={<DeleteIcon />}
          >
            {submitting ? Vocabulary.deleting : Vocabulary.deleteTicket}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketDetailsModal;
